import { BoxLandscapeGift } from '../BoxLandscapeGift';
import { MobileBoxNav } from './MobileBoxNav';
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import type { BoxInfo } from '@lub0v/santa-utils';
import { Stack } from '@src/components';
import { Footer } from '@src/components/Footer/Footer';
import type { BoxRoute } from '@src/routing/routes';
import { MobileLandscapeBackground } from '@src/components/Landscape/MobileLandscapeBackground';
import './BoxLayouts.css';

const subscribe = (callback: () => void) => {
  const media = window.matchMedia('(max-width: 48rem)');
  media.addEventListener('change', callback);
  return () => media.removeEventListener('change', callback);
};
export function useMobileBoxLayout() {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia('(max-width: 48rem)').matches,
    () => false,
  );
}

export function DesktopBoxLayout({ header, children }: { header: ReactNode; children: ReactNode }) {
  return (
    <Stack gap={5} testId="full-box">
      {header}
      {children}
    </Stack>
  );
}

export function MobileBoxLayout({
  info,
  view,
  landscapeForeground,
  footer = false,
  children,
}: {
  info: BoxInfo;
  view: BoxRoute['view'];
  landscapeForeground?: ReactNode;
  footer?: boolean;
  children: ReactNode;
}) {
  const layoutRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const menu = layout?.querySelector<HTMLElement>('.mobile-box-nav--bottom');
    if (!layout || !menu) return;
    const measure = () => layout.style.setProperty('--game-landscape-lift', `${menu.offsetHeight}px`);
    const observer = new ResizeObserver(measure);
    observer.observe(menu);
    measure();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewport = () => {
      // Safari reports transient viewport offsets during rubber-band scrolling.
      // Only follow the visual viewport when an editor has opened the keyboard.
      const editing = document.activeElement?.matches('input, textarea, [contenteditable="true"]');
      const keyboardOpen =
        editing && viewport && viewport.scale === 1 && window.innerHeight - viewport.height > 100;
      layoutRef.current?.style.setProperty('--mobile-viewport-width', `${window.innerWidth}px`);
      layoutRef.current?.style.setProperty('--mobile-viewport-left', '0px');
      layoutRef.current?.style.setProperty(
        '--mobile-viewport-height',
        `${keyboardOpen ? viewport.height : window.innerHeight}px`,
      );
      layoutRef.current?.style.setProperty(
        '--mobile-viewport-top',
        `${keyboardOpen ? Math.max(0, viewport.offsetTop) : 0}px`,
      );
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    document.addEventListener('focusin', updateViewport);
    document.addEventListener('focusout', updateViewport);
    viewport?.addEventListener('resize', updateViewport);
    viewport?.addEventListener('scroll', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      document.removeEventListener('focusin', updateViewport);
      document.removeEventListener('focusout', updateViewport);
      viewport?.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('scroll', updateViewport);
    };
  }, []);
  useEffect(() => {
    document.documentElement.classList.add('mobile-box-open');
    return () => document.documentElement.classList.remove('mobile-box-open');
  }, []);
  return (
    <div
      ref={layoutRef}
      className={`mobile-box-layout mobile-box-layout--${view}${footer ? ' mobile-box-layout--with-footer' : ''}`}
      data-testid="mobile-box-layout"
      data-app-version="v2"
    >
      {view !== 'main' && <MobileLandscapeBackground parallax={false} />}
      <div
        className={`mobile-box-layout__scroll${footer ? '' : ' full-box--mobile-nav'}`}
        data-testid="mobile-box-scroll"
      >
        {view === 'main' && (
          <MobileLandscapeBackground clear foreground={landscapeForeground} overlay={<BoxLandscapeGift key={info.box.key + ":" + info.cardId} info={info} />} />
        )}
        <main className="mobile-box-layout__content">{children}</main>
        {footer && <Footer />}
      </div>
      <MobileBoxNav info={info} view={view} />
    </div>
  );
}
